/**
 * Project status data for yan-vada PWA StatusPage.
 *
 * Typed constants derived from 06-data/*.json workspace files.
 * Single source of truth for the /status dashboard.
 *
 * Source files (workspace root):
 *   06-data/project-state.json
 *   06-data/tasks.json
 *   06-data/kei-indicators.json
 *   06-data/budget.json
 *   06-data/episodes.json
 */

/* ---------- Types ---------- */

export interface Blocker {
  text: string
  owner: string
  priority: string
}

export interface GateStatus {
  id: string
  label: string
  status: 'cleared' | 'pending'
}

export interface EpisodeStatus {
  id: string
  number: number
  title: string | null
  guest: string | null
  status: string
  researchStatus: string
}

export interface TaskSummary {
  total: number
  p0: number
  p1: number
  p2: number
  pending: number
  waiting: number
  blocked: number
  done: number
}

export interface BudgetSummary {
  ceilingEur: number
  spentEur: number
  committedEur: number
  remainingEur: number
  scopeNote: string
  futureLedgerNote: string
  categories: Array<{
    name: string
    spent: number
    committed: number
    description: string
  }>
}

export interface KEIDimension {
  name: string
  code: string
  weight: number
  description: string
  metrics: Array<{
    metric: string
    target: string
    current: string | number | null
  }>
}

export interface ProjectStatus {
  project: {
    name: string
    sinhalaName: string
    phase: string
    recordingWindow: string
    session: string
    lastUpdated: string
  }
  episodes: {
    planned: number
    recorded: number
    published: number
    list: EpisodeStatus[]
  }
  blockers: {
    active: Blocker[]
    resolved: string[]
  }
  gates: GateStatus[]
  tasks: TaskSummary
  budget: BudgetSummary
  kei: {
    dimensions: KEIDimension[]
    formula: string
    note: string
  }
  team: {
    active: string[]
    inTalks: string[]
  }
}

/* ---------- Data (from workspace JSON snapshots) ---------- */

export const projectStatus: ProjectStatus = {
  project: {
    name: 'Yan-Vada',
    sinhalaName: 'යන්-වාද',
    phase: 'Phase 1: Production Stabilization',
    recordingWindow: 'After 2026-04-25',
    session: 'Session 17.3 handover checkpoint',
    lastUpdated: '10 June 2026',
  },
  episodes: {
    planned: 4,
    recorded: 0,
    published: 0,
    list: [
      {
        id: 'EP-001',
        number: 1,
        title: 'Sound as Memory',
        guest: 'Dimuth K (DJ, confirmed)',
        status: 'research',
        researchStatus: 'partial — 9/10 sources',
      },
      { id: 'EP-002', number: 2, title: null, guest: null, status: 'planned', researchStatus: 'not started' },
      { id: 'EP-003', number: 3, title: null, guest: null, status: 'planned', researchStatus: 'not started' },
      { id: 'EP-004', number: 4, title: null, guest: null, status: 'planned', researchStatus: 'not started' },
    ],
  },
  blockers: {
    active: [
      { text: 'Sponsor logo geometry still drifting on live pages', owner: 'Riz + PJ', priority: 'P0' },
      { text: 'Two-tab sign-in state sync is unstable with full-signout/auth.yan.lk loop', owner: 'Riz + Aether', priority: 'P1' },
      { text: 'Resend/Slack transaction path still requires final Cloudflare credential wiring', owner: 'Riz + Nipun', priority: 'P1' },
      { text: 'Biometric authenticator priority policy is not enforced across fallback methods', owner: 'Riz', priority: 'P1' },
      { text: 'Domain pre-approval rule editing path needs one-screen admin UX for adding subdomains', owner: 'Riz + Tech', priority: 'P1' },
    ],
    resolved: [
      'Session 17 unified auth attempt-cookie fix on dev and main',
      '1:1 sponsor temporary pitch and value-report copies for The Licenced, Gold, Pop Culture, and Camera.lk',
      'Sponsor access panel compact CRM controls and filtering controls shipped',
      'Sneak-peek player route and Watch Sneak-Peek text deployed',
      'Yan-Vada subdomain vada.yan.lk ratified (D-059)',
      'Foundational pitch ratified (D-062)',
      'Dark mode override D-061 ratified',
      'Ratification protocol G4 cleared',
    ],
  },
  gates: [
    { id: 'G1', label: 'Dev auth cookie flow', status: 'cleared' },
    { id: 'G2', label: 'Sponsor pitch fallback copies', status: 'cleared' },
    { id: 'G3', label: 'Sneak-peek preview player', status: 'cleared' },
    { id: 'G4', label: 'Resend/Slack delivery hardening', status: 'pending' },
    { id: 'G5', label: 'Biometric-first auth prioritisation', status: 'pending' },
  ],
  tasks: {
    total: 30,
    p0: 7,
    p1: 14,
    p2: 9,
    pending: 14,
    waiting: 3,
    blocked: 2,
    done: 11,
  },
  budget: {
    ceilingEur: 7180,
    spentEur: 6023.80,
    committedEur: 1165.00,
    remainingEur: 991.20,
    scopeNote: 'This budget view tracks only the new Yan-Vada purchases currently being procured for production prep. Existing DGTL gear, software subscriptions, renewals, and shared infrastructure are not fully represented here yet.',
    futureLedgerNote: 'Next step: add editable line items and a depot-bot workflow that can reconcile two-way with Hasib and his budget databases before this becomes the full operating ledger.',
    categories: [
      { name: 'Cameras & Lenses', spent: 2517.00, committed: 0, description: 'G9 II, 25mm f/1.2, Meike 25mm' },
      { name: 'Audio', spent: 2234.40, committed: 0, description: 'SM7dB x2, RodeCaster, HA400, ATH-M30X' },
      { name: 'Lighting', spent: 635.60, committed: 450.00, description: 'Godox flash kit, VL150 pending' },
      { name: 'Filters', spent: 430.95, committed: 0, description: 'ND variable, ND fixed, Ethereal diffusion x2' },
      { name: 'Accessories', spent: 205.85, committed: 0, description: 'Battery, cables, adapters' },
      { name: 'Missing Critical', spent: 0, committed: 608.00, description: 'SD cards, tripod, power strip, cables' },
    ],
  },
  kei: {
    dimensions: [
      {
        name: 'Ship',
        code: 'S',
        weight: 2,
        description: 'Episodes produced, content published, guests booked',
        metrics: [
          { metric: 'Episodes recorded', target: '4', current: 0 },
          { metric: 'Episodes published', target: '4', current: 0 },
          { metric: 'Atomized clips', target: '12', current: 0 },
          { metric: 'Guests confirmed', target: '4', current: 0 },
        ],
      },
      {
        name: 'Velocity',
        code: 'V',
        weight: 1,
        description: 'Council output rate, decisions per session',
        metrics: [
          { metric: 'Decisions ratified/session', target: '5+', current: 0 },
          { metric: 'Documents produced/session', target: '3+', current: 0 },
          { metric: 'Days to publish', target: '7', current: null },
        ],
      },
      {
        name: 'Convergence',
        code: 'C',
        weight: 1,
        description: 'Prep output vs production readiness (primary pre-prod indicator)',
        metrics: [
          { metric: 'Pre-production tasks complete', target: '100%', current: '25%' },
          { metric: 'Knowledge verified', target: '80%+', current: '12%' },
          { metric: 'Equipment procurement', target: '100%', current: '70%' },
        ],
      },
      {
        name: 'Audience',
        code: 'A',
        weight: 1,
        description: 'Listener satisfaction, content quality (post-Ep1)',
        metrics: [
          { metric: 'Listener satisfaction', target: '4.0/5.0+', current: null },
          { metric: 'Retention rate (>50%)', target: '60%+', current: null },
        ],
      },
      {
        name: 'Sovereignty',
        code: 'Sig',
        weight: 1,
        description: 'Content owned on DGTL infrastructure',
        metrics: [
          { metric: 'DGTL-hosted content %', target: '100%', current: null },
          { metric: 'Platform lock-ins', target: '0 critical', current: null },
        ],
      },
    ],
    formula: 'KEI = (2S + V + C + A + Sig) / 6',
    note: 'KEI scoring begins after Episode 1 data is available. Convergence is the primary pre-production indicator.',
  },
  team: {
    active: ['Riz Razak (host, founder)', 'PJ (project manager)', 'Kalpanee Gunawardana'],
    inTalks: ['Rehan A', 'Portia Ratnayake', 'Shane Karvin', 'Pasindu De Silva', 'Hiruni'],
  },
}
